import { Building, BuildingFloorplans } from '../../apolloClient/types';
import { BuildingStats } from '../../apolloClient/types/Building';
import { ORIGIN } from 'src/constants';
import { NO_DATA_PLACEHOLDER } from '../../constants/labels';
import { Listing } from '../../apolloClient/types/Units';
import {
  getATTFromLocalStorage,
  numberFormatter,
  formatNumberOrRangeSafe,
  getListingUnitIdentifier,
} from './utils';

type getBuildingFloorplansArguments = BuildingFloorplans[] | undefined;

export type NormalizedBuildingFloorplan = {
  [key: string]: number | string;
  aptNo: string;
  model: string;
  bedBath: string;
  url: string;
  floors: any;
  interiorExt: string;
};

export function getBuildingFloorplans(
  floorplansData: getBuildingFloorplansArguments
): NormalizedBuildingFloorplan[] {
  const filterFloorplan = floorplansData !== undefined ? floorplansData : [];
  return filterFloorplan.map((floorplan: any) => {
    return {
      aptNo:
        floorplan.apartmentNumbers !== null
          ? floorplan.apartmentNumbers
          : NO_DATA_PLACEHOLDER,
      model: floorplan.model || NO_DATA_PLACEHOLDER,
      bedBath: floorplan.bedBath || NO_DATA_PLACEHOLDER,
      url: floorplan.image?.data?.attributes?.url || '',
      floors:
        floorplan.floors
          .filter((floor: any) => floor.from > 0)
          .map((floor: any) =>
            floor.from === floor.to
              ? floor.from
              : `${floor.from > 0 ? floor.from : ''} ${
                  floor.from > 0 && floor.to > 0 ? '-' : ''
                } ${floor.to > 0 ? `${floor.to}` : ''}`
          )
          .join(', ') || NO_DATA_PLACEHOLDER,
      interiorExt:
        (floorplan.area
          ? numberFormatter(floorplan.area)
          : NO_DATA_PLACEHOLDER) +
        ' + ' +
        (floorplan.exteriorArea
          ? formatNumberOrRangeSafe(floorplan.exteriorArea)
          : NO_DATA_PLACEHOLDER),
    };
  });
}

export function streetDirectionFormat(
  streetDirection: string | null | undefined
) {
  if (!streetDirection || typeof streetDirection !== 'string') {
    return '';
  }

  const dataStreetDirection = Object.assign([], streetDirection);

  return dataStreetDirection
    .filter((x: any) => x === x.toUpperCase())
    .join('')
    .replace('_', '');
}

export function getContactFormContextData(
  building: undefined | Building,
  listing: undefined | Listing,
  buildingPageUrl: undefined | String,
  unitPageUrl: undefined | String,
  pageUrl: undefined | String
) {
  const cityName =
    building?.neighborhood?.data?.attributes?.city?.data?.attributes?.name;

  const { streetNumber, streetDirection, streetName, zipCode, streetType } =
    listing?.address || {};

  const updatedStreetDirection = streetDirectionFormat(streetDirection);

  let streetTypeToDisplay = '';

  if (streetType && streetType !== 'Unknown') {
    streetTypeToDisplay = streetType;
  }

  const unitNumber = getListingUnitIdentifier(listing);

  const listingAddress = listing?.address
    ? `${streetNumber} ${
        updatedStreetDirection || ''
      } ${streetName} ${streetTypeToDisplay} # ${unitNumber}, ${cityName}, FL ${zipCode}`
    : '';

  const trimmedListingAddress = listingAddress.replace(/\s+/g, ' ').trim();

  const address =
    trimmedListingAddress || building?.primaryAddress || document.title || '';

  return {
    BuildingName: building?.name ?? '',
    //TODO: when we will have a logic for metatags (title) - make using title metatag OR specific pageTitle definition for current CBB page type (refer to legacy site)
    PageName: document.title ?? '',
    LeadType:
      listing?.propertyTypeCode !== undefined
        ? listing?.propertyTypeCode === 'R'
          ? 'RENT'
          : 'SALE'
        : '',
    BuildingAndUnit: building?.name
      ? `${building?.name}${unitNumber ? ` #${unitNumber}` : ''}`
      : '',
    Neighborhood: building?.neighborhood?.data?.attributes?.name ?? '',
    City: cityName ?? '',
    UnitPrice: listing?.price?.toString() ?? '',
    UnitPriceInt: listing?.price ?? null,
    UnitUrl: unitPageUrl ?? buildingPageUrl ?? '', //TODO: on unit page - get current page URL
    UnitAddress: address, //TODO: building page and unit page: make backend logic for getting address; for building page - use {Address.StreetNumber} {Address.StreetName}; for unit - take its address*/
    UnitNumber: getListingUnitIdentifier(listing),
    UnitZip:
      listing?.address?.zipCode ?? building?.mappingAddresses?.[0]?.zipCode ?? '',
    PpcUser: getATTFromLocalStorage() ? true : false, //TODO: when PPC logic will be ready - take from localStorage.getItem("ppc_user") (refer to legacy site)
    BuildingAvgPrice: building?.buildingStats?.averageSalePrice,
    IsPreConstruction: building?.isPreconstruction,
    pageUrl: ORIGIN && pageUrl ? `${ORIGIN}${pageUrl}` : ORIGIN,
    MlsNumber: listing?.mlsListingId ?? '',
    PropertyType: listing?.propertyTypeCode ?? '',
    BuildingSlug: building?.slug ?? '',
    ListOfficeMlsId: listing?.listOfficeMlsId ?? '',
    MlsOfficeName: listing?.mlsOfficeName ?? '',
  };
}

/**
 * Average price for the building's schema.org Product offer.
 * Falls back to sold units (past 6 months) when there are no active sale listings.
 */
export function getBuildingSchemaPrice(
  buildingStats: Pick<
    BuildingStats,
    'averageSalePrice' | 'averageSalePriceSixMonths'
  > | null | undefined
): number | undefined {
  const averagePrice =
    buildingStats?.averageSalePrice || buildingStats?.averageSalePriceSixMonths;

  return averagePrice ? Math.round(averagePrice) : undefined;
}
